import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/apiService';

export type Product = {
  PROId: number;
  isin: string;
  valor?: string;
  name: string;
  bid: number;
  ask: number;
  ccy: string;
  issuer?: string;
  productType?: number;
  productCode?: string;
  initialFixing?: string;
  finalFixing?: string;
  redemptionDate?: string;
  tradedNotional?: number | null;
  currentNotional?: number | null;
  livePrice?: boolean;
  strikePct?: number;
  worstStrikeUnderlyingName?: string;
  worstStrikeUnderlyingWofPct?: number | null;
  worstStrikeUnderlyingDistancePct?: number | null;
  worstStrikeUnderlyingCurrent?: number | null;
  worstStrikeUnderlyingStrike?: number | null;
  worstBarrierUnderlyingName?: string;
  worstBarrierUnderlyingWofPct?: number | null;
  worstBarrierPct?: number | null;
  worstBarrierUnderlyingDistancePct?: number | null;
  worstBarrierUnderlyingCurrent?: number | null;
  worstBarrierUnderlyingBarrier?: number | null;
  barrierStyleName?: string | null;
  productTypeDisplay?: string;
  // Event-related fields (only populated when filtering by events)
  nextEventDate?: string;
  eventType?: string | null;
  worstUnderlyingName?: string;
  distanceFromAutocall?: number | null;
  allocatedNotional?: number | null;
  maxNotional?: number | null;
};

export type FilterType = 'date' | 'product' | 'category';

export function useProductsList(
  initialFilters: {
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    eventBucket?: 'nextWeek' | 'nextMaturity';
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    ownershipScope?: 'group' | 'self';
    maxDistancePct?: number;
    productTypes?: string[];
    issuers?: string[];
    selectedProductTypes?: string[];
    selectedIssuers?: string[];
    includeWarrants?: boolean;
  } | null
) {
  const [activeFilter, setActiveFilter] = useState<FilterType>('date');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardFilters, setDashboardFilters] = useState(initialFilters || null);

  // Nota: la paginación incremental se eliminó para mostrar TODOS los resultados en una sola página.
  // Aumentamos el pageSize para reducir número de requests.
  const pageSize = 100;
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);

  const loadAllProducts = useCallback(
    async (isRefresh: boolean = false) => {
      const actualSearchText = searchQuery;
      const actualFilterType = activeFilter;

      if (loadingRef.current) return;
      loadingRef.current = true;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const productMap = new Map<number, Product>();

        let currentPage = 1;
        let hasNext = true;
        let safetyMaxPages = 10_000; // Evita loops infinitos en caso de inconsistencia del backend

        while (hasNext && currentPage <= safetyMaxPages) {
          const response = await apiService.getProductsList(currentPage, pageSize, {
            searchQuery: actualSearchText || undefined,
            filterType: actualFilterType,
            currency: dashboardFilters?.currency,
            issuer: dashboardFilters?.issuer,
            maturityQuarter: dashboardFilters?.maturityQuarter,
            underlying: dashboardFilters?.underlying,
            eventType: dashboardFilters?.eventType,
            weekStart: dashboardFilters?.weekStart,
            eventDate: dashboardFilters?.eventDate,
            eventBucket: dashboardFilters?.eventBucket,
            nearBarrier: dashboardFilters?.nearBarrier,
            riskType: dashboardFilters?.riskType,
            ownershipScope: dashboardFilters?.ownershipScope || 'group',
            maxDistancePct: dashboardFilters?.maxDistancePct,
            productTypes: dashboardFilters?.productTypes,
            issuers: dashboardFilters?.issuers,
            includeWarrants: dashboardFilters?.includeWarrants,
          });

          const newProducts: Product[] = response.data || [];
          newProducts.forEach(p => productMap.set(p.PROId, p));

          hasNext = response.pagination?.hasNextPage || false;
          const totalPagesFromBackend = response.pagination?.totalPages;
          if (typeof totalPagesFromBackend === 'number' && totalPagesFromBackend > 0) {
            safetyMaxPages = Math.min(safetyMaxPages, totalPagesFromBackend);
          }

          currentPage += 1;

          // Si durante la carga cambian filtros/búsqueda, no actualizamos el estado viejo.
          if (requestIdRef.current !== requestId) return;
        }

        setProducts(Array.from(productMap.values()));
      } catch (err: any) {
        setError(err.message || 'Error loading products');
      } finally {
        setLoading(false);
        setRefreshing(false);
        loadingRef.current = false;
      }
    },
    [activeFilter, dashboardFilters, pageSize, searchQuery]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAllProducts(false);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, activeFilter, loadAllProducts]);

  useEffect(() => {
    loadAllProducts(false);
  }, [dashboardFilters, activeFilter, loadAllProducts]);

  const handleRefresh = () => {
    loadAllProducts(true);
  };

  return {
    // state
    searchQuery,
    products,
    loading,
    refreshing,
    error,
    dashboardFilters,
    setDashboardFilters,
    // actions
    setSearchQuery,
    handleRefresh,
  };
}
